/**
 * App.tsx — las rutas, y la puerta.
 *
 * La aplicación NO se monta sin sesión y sin empresa elegida. No es comodidad
 * de enrutamiento: el cascarón entero da por hecho que hay una empresa activa,
 * y montarlo sin ella produce pantallas que consultan sin contexto y vuelven
 * vacías — que se ve exactamente igual que «no hay datos».
 *
 * Y conviene decirlo, porque se asume al revés: **esto no es el control de
 * acceso.** Quien decide es la base, en cada petición, comprobando la
 * membresía. Esto solo evita enseñar una aplicación que no va a poder
 * responder.
 *
 * ▶ Y desde el 11 de setiembre **todas las entradas del menú llevan a una
 * pantalla de verdad.** Hasta esa fecha las que faltaban caían en
 * `EnConstruccion`, que decía en qué etapa entraban — un menú a medias esconde
 * el mapa del producto justo cuando sirve para enseñarlo.
 *
 * Con `/configuracion` construida no quedaba ninguna, así que esa pantalla se
 * borró en vez de dejarla esperando: un componente sin llamador es lo que el
 * guardián 8 llama una promesa que nadie cobra, y su texto habría seguido
 * diciendo «fuera del MVP» sobre una ruta que ya existe.
 *
 * Si mañana nace una entrada sin pantalla, vuelve — y con ella la regla de que
 * un número de etapa envejece el día que la etapa cierra.
 */
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Cascaron } from './componentes/Cascaron.tsx';
import { Tablero } from './paginas/Tablero.tsx';
import { Diario } from './paginas/Diario.tsx';
import { Catalogo } from './paginas/Catalogo.tsx';
import { Contrapartes } from './paginas/Contrapartes.tsx';
import { Activos } from './paginas/Activos.tsx';
import { Diferidos } from './paginas/Diferidos.tsx';
import { Proyectos } from './paginas/Proyectos.tsx';
import { Apertura } from './paginas/Apertura.tsx';
import { Cierre } from './paginas/Cierre.tsx';
import { Bitacora } from './paginas/Bitacora.tsx';
import { Integraciones } from './paginas/Integraciones.tsx';
import { Facturas } from './paginas/Facturas.tsx';
import { Acceso } from './paginas/Acceso.tsx';
import { useLocation } from 'react-router-dom';
import { useIdioma } from './nucleo/idioma.tsx';
import { useSesion } from './nucleo/sesion.tsx';
import { Clave } from './paginas/Clave.tsx';
import { Usuarios } from './paginas/Usuarios.tsx';
import { ProveedorDeCatalogo } from './nucleo/catalogo.tsx';
import { ProveedorDeMonedas } from './nucleo/monedas.tsx';

/* ▶▶ LAS PESADAS ENTRAN EN UN CHUNK APARTE, Y POR QUÉ
   ═══════════════════════════════════════════════════════════════════════════

   Seis páginas suman ~7 000 líneas de JSX de las ~14 000 totales. Si todas
   entran en el bundle inicial, el móvil con red flaca se come 1,3 MB antes de
   ver el tablero —y la mitad de ese peso es código que no va a abrir en la
   sesión—. `React.lazy` las corta en archivos propios que se piden al navegar;
   el `Suspense` de abajo cubre el instante que tarda la bajada.

   ▶ Las ligeras (Tablero, Diario, Catálogo, Contrapartes, Activos, Diferidos,
     Proyectos, Apertura, Cierre, Bitácora, Integraciones, Facturas,
     Usuarios) se quedan en el bundle principal. Separarlas también serían
     chunks de 20-50 KB cada uno — más viajes de red, menos ganancia por viaje.
     El criterio es *«separá sólo lo que valga la pena pedir aparte»*. */
const Asiento = lazy(() => import('./paginas/Asiento.tsx').then(m => ({ default: m.Asiento })));
const Documentos = lazy(() => import('./paginas/Documentos.tsx').then(m => ({ default: m.Documentos })));
const Banco = lazy(() => import('./paginas/Banco.tsx').then(m => ({ default: m.Banco })));
const Configuracion = lazy(() => import('./paginas/Configuracion.tsx').then(m => ({ default: m.Configuracion })));
const Reportes = lazy(() => import('./paginas/Reportes.tsx').then(m => ({ default: m.Reportes })));
const Consola = lazy(() => import('./paginas/Consola.tsx').then(m => ({ default: m.Consola })));

/* El fallback del Suspense: lo que la pantalla enseña mientras se baja el
   chunk. En red local es imperceptible; en red lenta son 300-800 ms. La
   frase respeta el idioma. */
function Cargando() {
  const { t } = useIdioma();
  return <p className="muted" style={{ padding: 'var(--s7)' }}>{t('common.loading')}</p>;
}

export function App() {
  const { pantalla } = useSesion();
  const { pathname } = useLocation();

  /* ▶ La consola vive FUERA del cascarón y antes de elegir empresa.
   *
   * Es la única pantalla autenticada que no necesita una empresa activa, y no
   * puede necesitarla: su razón de ser es que crear un cliente NO da acceso a
   * sus libros. Meterla dentro del cascarón «para que tenga el menú» obligaría
   * al operador a elegir una empresa, y ahí se acabó la garantía.
   *
   * Basta con estar autenticado —hay token y ya se sabe qué empresas hay—; si
   * quien entra no es operador, la propia pantalla lo dice, y la base rechaza
   * cada petición igual. */
  /* ▶ `/clave` va ANTES de todo, incluso de saber si hay sesión.
   *
   * Quien llega ahí viene de un correo —invitación o recuperación— y trae la
   * credencial en la URL. Si esta comprobación fuera después, pasarían dos
   * cosas y las dos malas: sin sesión se le enseñaría el formulario de acceso
   * pidiéndole una contraseña que todavía no tiene, y CON sesión —porque el
   * enlace ya la trajo— se le metería directo en la aplicación sin dejarle
   * ponerla nunca.
   *
   * Es la ruta que faltaba desde E8.1: se automatizó invitar y no había
   * pantalla que recibiera al invitado. */
  if (pathname === '/clave') return <Clave />;

  const autenticado = pantalla === 'aplicacion'
    || pantalla === 'elegir_empresa' || pantalla === 'sin_empresas';
  if (pathname === '/consola' && autenticado) {
    // La consola también es lazy (813 líneas) — envuelta en su propio
    // Suspense porque vive fuera del árbol de rutas de abajo.
    return <Suspense fallback={<Cargando />}><Consola /></Suspense>;
  }

  if (pantalla !== 'aplicacion') return <Acceso />;

  return (
    <ProveedorDeCatalogo>
      <ProveedorDeMonedas>
      <Suspense fallback={<Cargando />}>
      <Routes>
      <Route element={<Cascaron />}>
        <Route index element={<Tablero />} />
        <Route path="asientos" element={<Asiento />} />
        <Route path="diario" element={<Diario />} />
        <Route path="catalogo" element={<Catalogo />} />
        <Route path="contrapartes" element={<Contrapartes />} />
        {/* ▶ La misma pantalla los dos lados. Ver `ROTULOS` en
            `Documentos.tsx`: copiarla habría sido setecientas líneas
            que divergen al primer arreglo. */}
        <Route path="por-pagar" element={<Documentos papel="pagar" />} />
        <Route path="por-cobrar" element={<Documentos papel="cobrar" />} />
        <Route path="apertura" element={<Apertura />} />
        <Route path="activos" element={<Activos />} />
        <Route path="diferidos" element={<Diferidos />} />
        <Route path="proyectos" element={<Proyectos />} />
        <Route path="banco" element={<Banco />} />
        <Route path="cierre" element={<Cierre />} />
        <Route path="bitacora" element={<Bitacora />} />
        <Route path="facturas" element={<Facturas />} />
        <Route path="reportes" element={<Reportes />} />
        <Route path="integraciones" element={<Integraciones />} />
        <Route path="usuarios" element={<Usuarios />} />
        {/* ▶ Decía `etapa="E6"`, y E6 cerró. La pantalla anunciaba su trabajo
            dentro de una etapa TERMINADA — o sea que quien la abría podía
            concluir, con razón, que quedaba algo de E6 sin entregar.
            Configuración está en los mockups de la Fase 0 y no tiene etapa en
            el plan de E0–E9, así que va sin número: ponerle uno es prometer un
            calendario que nadie decidió. */}
        <Route path="configuracion" element={<Configuracion />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      </Routes>
      </Suspense>
      </ProveedorDeMonedas>
    </ProveedorDeCatalogo>
  );
}
